// Budget vs. actual on the Google & Meta spend page: each client's spend against its budget over the last 7, 14 and 30
// days, red/yellow/green. Supabase (dashboard_budget_pacing) refuses managers and clients; campaigns linked to no client
// come back for super admins only.
import {useEffect,useState} from 'react';
import {supabase} from './supabase';
import {portalError} from './portal-model';
import {dateLabel,money} from './business-kpis-model';
import {WINDOWS,dailyBudget,filterByStatus,sortByPace,sources,statusLabel,tally,windowOf,
 type BudgetPacing as Pacing,type PaceStatus,type PaceWindow} from './budget-pacing-model';
import './budget-pacing.css';

function Cell({w}:{w:PaceWindow}){
 if(w.status==='none') return <td className="bpace-cell bpace-cell--none"><span className="bpace-dot bpace-dot--none" aria-hidden="true"/>No budget or spend</td>;
 return <td className={`bpace-cell bpace-cell--${w.status}`}>
  <span className={`bpace-dot bpace-dot--${w.status}`} aria-hidden="true"/>
  <strong>{w.pct===null?'No budget':`${w.pct}%`}</strong>
  {w.estimated&&<abbr className="bpace-est" title="Estimated: part of this window is from before Google and Meta budgets were recorded (8 Oct)">est.</abbr>}
  <small>{money(w.actual)} of {money(w.budget)}</small>
  <span className="sr-only">{statusLabel[w.status]}</span>
 </td>;
}

export function BudgetPacing(){
 const [data,setData]=useState<Pacing|null>(null),[error,setError]=useState(''),[busy,setBusy]=useState(true);
 const [days,setDays]=useState<number>(7),[status,setStatus]=useState<PaceStatus|'all'>('all');
 useEffect(()=>{let live=true;
  void Promise.resolve(supabase.rpc('dashboard_budget_pacing',{p_agency:null,p_clients:null})).then(r=>{
   if(!live)return;if(r.error)setError(portalError(r.error));else setData(r.data as Pacing);setBusy(false);});
  return()=>{live=false;};},[]);
 const counts=data?tally(data.clients,days):null;
 const rows=data?filterByStatus(sortByPace(data.clients,c=>c.total),status,days):[];
 const campaigns=data?.unassigned?sortByPace(data.unassigned,u=>u.windows):[];
 return <section className="section bpace" aria-labelledby="bpace-title" aria-busy={busy}>
  <div className="section__head"><div><h2 id="bpace-title">Budget vs. actual</h2></div>
   <p>Each client’s spend against its budget, to {data?dateLabel(data.as_of):'yesterday'}. The budget is the client’s plan for the month on the Clients page, else the daily budgets set in Google and Meta. Green 90–110% of budget, yellow 75–90% or 110–125%, red outside that or spend with no budget.</p></div>
  {error&&<p role="alert" className="form-error">{error}</p>}
  {!data&&busy&&<p role="status">Loading budgets…</p>}
  {data&&<>
   <div className="bpace-bar">
    <div className="bpace-filters" role="group" aria-label="Show clients">
     <button type="button" className="bpace-filter" aria-pressed={status==='all'} onClick={()=>setStatus('all')}>All {data.clients.length}</button>
     {(['red','yellow','green'] as const).map(s=><button key={s} type="button" className={`bpace-filter bpace-filter--${s}`} aria-pressed={status===s} onClick={()=>setStatus(s)}>
      <span className={`bpace-dot bpace-dot--${s}`} aria-hidden="true"/>{statusLabel[s]} {counts?.[s]??0}</button>)}
    </div>
    <label className="bkpi-period">Over<select value={days} onChange={e=>setDays(Number(e.target.value))}>{WINDOWS.map(d=><option key={d} value={d}>the last {d} days</option>)}</select></label>
   </div>
   <div className="table-wrap bkpi-table bpace-table" tabIndex={0} role="region" aria-label="Budget vs. actual by client"><table>
    <thead><tr><th scope="col">Client</th><th scope="col">Daily budget now</th>{WINDOWS.map(d=><th scope="col" key={d}>Last {d} days</th>)}</tr></thead>
    <tbody>{rows.map(c=>{const daily=dailyBudget(c);return <tr key={c.business_id}>
     <th scope="row">{c.name}<small>{sources(c)}</small></th>
     <td>{daily===null?'—':money(daily,true)}</td>
     {WINDOWS.map(d=><Cell key={d} w={windowOf(c.total,d)}/>)}
    </tr>;})}</tbody>
   </table></div>
   {!rows.length&&<p className="empty empty--light">{status==='all'?'No client has a budget or spend in the last 30 days.':`No client is ${statusLabel[status].toLowerCase()} over the last ${days} days.`}</p>}
   {data.budgets_recorded_from&&<p className="bpace-note">Google and Meta budgets are recorded every morning from {dateLabel(data.budgets_recorded_from)}. Before that, <abbr title="estimated">est.</abbr> windows use each campaign’s first recorded budget on the days it ran.</p>}
   {campaigns.length>0&&<>
    <h3 className="bkpi-subhead">Campaigns linked to no client</h3>
    <p className="bpace-note">Their spend counts for no client until they are linked to a website below. Super admins only.</p>
    <div className="table-wrap bkpi-table bpace-table" tabIndex={0} role="region" aria-label="Budget vs. actual for campaigns linked to no client"><table>
     <thead><tr><th scope="col">Campaign</th><th scope="col">Daily budget now</th>{WINDOWS.map(d=><th scope="col" key={d}>Last {d} days</th>)}</tr></thead>
     <tbody>{campaigns.map(u=><tr key={`${u.platform}-${u.campaign_id}`}>
      <th scope="row">{u.name}<small>{u.platform}</small></th>
      <td>{u.daily_budget===null?'Paused':money(u.daily_budget,true)}</td>
      {WINDOWS.map(d=><Cell key={d} w={windowOf(u.windows,d)}/>)}
     </tr>)}</tbody>
    </table></div>
   </>}
  </>}
 </section>;
}
